import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { Activity, Hostage, AttendActivityResponse } from "../types";
import "../../style/EventCard.css";
import { useTranslation } from "react-i18next";
import JoinedActivityDrawer from "./JoinedActivityDrawer";
import { Share, Delete, Edit } from "@mui/icons-material";
import { useUser } from "../../context/UserContext";
import ReactGA from "react-ga4";
import ActivityForm from "./ActivityForm";
import usersCountIcon from "../../icons/usersCount.svg";
import Linkify from "react-linkify";
import { HostagePageLink } from "../HostagePageLink";


type Props = {
  activity: Activity,
  hostage: Hostage,
  eventOwner: Hostage
  showLink?: boolean,
  numOfRegisteredUsers: number,
  showReadMore?: boolean,
  onRefresh?: () => void,
  onJoin?: (activityId: string) => void;
  onUndoJoin?: (activityId: string) => void;
  onClick?: () => void;
}

const formatDate = (isoDate: string) => {
  try {
    const date = new Date(isoDate);

    const formattedDate = new Intl.DateTimeFormat("en-IL", {
      timeZone: "Asia/Jerusalem",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);

    const formattedTime = new Intl.DateTimeFormat("en-IL", {
      timeZone: "Asia/Jerusalem",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);

    return `${formattedDate} | ${formattedTime}`;
  } catch (err) {
    console.error("Error formatting date:", err);
    return "ללא תאריך";
  }
};

const ActivityCard = ({ activity: activity, hostage, eventOwner, numOfRegisteredUsers, onRefresh, onJoin, onUndoJoin, onClick, showLink=false, showReadMore=true }: Props) => {
  const { t, i18n } = useTranslation();
  const { user, updateRegisteredActivities } = useUser();
  const [isJoinedDrawerOpen, setIsJoinedDrawerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [canJoinCurrentStatus, setCanJoinCurrentStatus] = useState<boolean | null>(null);
  const [isAddActivityDrawerOpen, setIsAddActivityDrawerOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const maxTextLength = 130; // description max shown length
  const activityTitle = activity[i18n.language as "en" | "he"]?.title;
  const activityDescription = activity[i18n.language as "en" | "he"]?.description;
  const activityLocation= activity[i18n.language as "en" | "he"]?.location;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          text: t("eventCard.shareText", { activityName: activityTitle }),
          url: window.location.href,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    }
  };

  function canEdit() {
    return user?.role === "admin" ||
      (user?.role === "moderator" && eventOwner?.id && user?.moderating?.includes(eventOwner.id));
  }

  function canDelete() {
    return user?.role === "admin" ||
      (user?.role === "moderator" && hostage?.id && user?.moderating?.includes(hostage?.id));
  }


  function canJoin() {
    if (canJoinCurrentStatus !== null) {
      return canJoinCurrentStatus;
    }

    return !user?.registered_activities?.map((ev: Activity) => ev.id).includes(activity.id);
  }

  const confirmDelete = () => {
    fetch(`/admin/activity/${activity.id}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "X-HOSTAGE-ID": hostage?.id,
      },
    })
      .catch(err => {
        console.error("Error deleting event:", err);
      })
      .finally(() => {
        setIsDeleteDialogOpen(false); // Close the dialog after deletion
        if (onRefresh) {
          onRefresh();
        }
      });
  };

  const handleJoin = async () => {
    try {
      const url = "/api/user/attendActivity";
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ activityId: activity.id }),
      });
      const res = await response.json() as AttendActivityResponse;
      console.log("Joined event:", res);
      // @ts-ignore
      const updatedActivities = [...user.registered_activities, activity];
      updateRegisteredActivities(updatedActivities);

      setIsJoinedDrawerOpen(true);
      setCanJoinCurrentStatus(false);

      ReactGA.event({
        category: "Event",
        action: "Join Event Clicked",
        label: activityTitle,
      });

    } catch (err) {
      console.error("Error joining event:", err);
    }
  };

  const handleUndoJoin = async () => {
    try {
      const url = "/api/user/unattendActivity";
      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ activityId: activity.id }),
      });
      const res = await response.json() as AttendActivityResponse;
      console.log(("Unattend event"), res);

      setCanJoinCurrentStatus(true);

      const updatedActivities = user?.registered_activities?.filter(ac => ac.id !== activity.id);
      if (updatedActivities) updateRegisteredActivities(updatedActivities);

      ReactGA.event({
        category: "Event",
        action: "Unattend Event Clicked",
        label: activityTitle,
      });

      if (onUndoJoin && activity.id) {
        onUndoJoin(activity.id);
      }

    } catch (err) {
      console.error("Error joining event:", err);
    }
  };

  const handleToggleJoin = async () => {
    if (canJoin()) {
      await handleJoin();
    } else {
      await handleUndoJoin();
    }
  };

  const defaultImage = "https://www.israelhayom.co.il/wp-content/uploads/2024/02/17/17/WhatsApp-Image-2024-02-17-at-18.45.09.jpeg";
  const image = activity.photo ? activity.photo : defaultImage;

  function handleEditActivity(): void {
    setIsAddActivityDrawerOpen(true);
  }

  const handleToggleReadMore = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const activityDescriptionShort = showReadMore && activityDescription.length > maxTextLength && !isDescriptionExpanded
    ? `${activityDescription.substring(0, maxTextLength)}...`
    : activityDescription;

  function closeIsJoinedDrawer(): void {
    setIsJoinedDrawerOpen(false);
    if (onJoin && activity.id) {
      onJoin(activity.id);
    }
  }

  return (
    <Card className="event-container">
      <Box className="event-image-container" onClick={onClick}>
        <CardMedia
          className="event-image"
          component="img"
          height="auto"
          image={image}
          alt="event"
        />
        <Box className="event-overlay">
          <div className="share-btn" onClick={handleShare}>
            <Share />
          </div>
          {canDelete() &&
            <div className="delete-btn" onClick={() => setIsDeleteDialogOpen(true)}>
              <Delete />
            </div>}
          {canEdit() &&
            <div className="edit-btn" onClick={() => handleEditActivity()}>
              <Edit />
            </div>}
        </Box>
      </Box>
      <ActivityForm
        open={isAddActivityDrawerOpen}
        onClose={() => {
          setIsAddActivityDrawerOpen(false);
          window.location.reload();
        }}
        hostage={hostage}
        activity={activity}
      />
      <CardContent className="event-card-content">
        <Box className="event-details">
          {activity.date && <Typography className="event-time" variant="body2" component="div">
            {formatDate(activity.date)}
          </Typography>}
          <Typography className="event-location" variant="body2" component="div">
            {activityLocation}
          </Typography>
        </Box>
        <Typography className="event-name" variant="h6" component="div">
          {activityTitle}
        </Typography>
        <Typography variant="body2" color="textSecondary" align="right" whiteSpace="pre-wrap">
          <Linkify>{activityDescriptionShort}</Linkify>
          {showReadMore && activityDescription.length > maxTextLength && (
            <Button onClick={handleToggleReadMore} className="show-more-or-less">
              {isDescriptionExpanded ? `...${t("story.readLess")}` : `${t("story.readMore")}`}
            </Button>
          )}
          {showLink &&
            <HostagePageLink hostageId={activity.hostages[0]} hostageName={hostage ? hostage[i18n.language as "en" | "he"].name_with_title : ""}/>
        }
        </Typography>
        <Box display="flex" alignItems="center" justifyContent="space-between" marginTop="16px">
          <Button
            className={canJoin() ? "join-btn" : "already-joined-btn" }
            variant="contained"
            onClick={handleToggleJoin}
          >
            {!canJoin() && <CheckIcon style={{ marginLeft: "8px" }} />}
            {canJoin() ? t("eventCard.joinBtn") : t("eventCard.alreadyJoined")}
          </Button>
          <Box display="flex" alignItems="center">
            <img src={usersCountIcon} alt="users count" className="users-count-icon" />
            <Box display="flex" flexDirection="column" textAlign="left">
              <Typography variant="body2" color="textPrimary" style={{ lineHeight: "1", alignSelf: "center" }}>
                {numOfRegisteredUsers}
              </Typography>
              <Typography variant="body2" color="textPrimary" style={{ lineHeight: "1", color: "#727272" }}>
                {t("eventCard.registeredUsers")}
              </Typography>
            </Box>
          </Box>
        </Box>
        <JoinedActivityDrawer
          open={isJoinedDrawerOpen}
          onClose={() => closeIsJoinedDrawer()}
          handleShare={handleShare}
          numOfUsers={numOfRegisteredUsers}
          hostage={hostage}
          activity={activity}
        />
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("eventCard.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("eventCard.deleteConfirmText")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="primary">
            {t("eventCard.cancelBtn")}
          </Button>
          <Button onClick={confirmDelete} color="secondary" autoFocus>
            {t("eventCard.confirmBtn")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ActivityCard;
